import { toast } from "react-toastify";
import { HttpStatusCode, isAxiosError } from "axios";
import * as Sentry from "@sentry/react";
import useSWRMutation from "swr/mutation";
import { useApiClient } from "@/hooks/useApiClient";
import { SAMBO_KUN_PARTNER_ID } from "@/features/partner/constants";
import { sendPropertyAndOwnerInfo } from "@/features/partner/api/sendToPartner";
import { navigateTo } from "@/features/partner/utils/navigation";
import { SendToTrackingService } from "@/api/handlers/errors";
import {
  type SendToPartnerRequest,
  type SendToPartnerResponse,
} from "@/features/partner/types";

type SendTarget =
  | {
      target: "owner";
      ownerAnalyzeRequestId: number;
      excludedPictureRequestDetailIds?: number[];
    }
  | {
      target: "allMatters";
      allMattersAnalyzeRequestId: number;
      excludedPictureRequestDetailIds?: number[];
    };

interface SendResult {
  /**
   * 有効な (https:) リダイレクト URL が 2 件以上返ってきた場合、その配列。
   * 0 件 / 1 件の場合は空配列が入る（それぞれ toast / auto-redirect で処理済み）。
   * 呼び出し側はこれが空でない場合のみ「ユーザーが選んで開く」UI を出す責務を持つ。
   */
  multipleRedirectUris: string[];
}

interface UseSendToSamboKunResult {
  send: (params: SendTarget) => Promise<SendResult>;
}

/**
 * 参謀くん連携APIを呼び出す hook。
 *
 * 内部で `useSWRMutation` を使い POST を発行する。SWR のキーは
 * `/partner/${SAMBO_KUN_PARTNER_ID}/property-and-owner-info` 固定のため、同キーで
 * マウントされる複数インスタンス間で SWR の内部状態（`isMutating` 等）は共有される。
 * 本 hook では `trigger()` の戻り値 Promise のみ参照し、共有状態は使わないため、
 * 呼び出しごとに独立した結果を受け取れる（運用上も Dialog は同時に 1 つしか開かない）。
 *
 * 通知ポリシー:
 *  - 200 + success=true + 有効な redirectUri が 1 件: リダイレクト（toast/Sentry なし。遷移そのものが成功フィードバック）
 *  - 200 + success=true + 有効な redirectUri が 2 件以上: リダイレクトせず、`multipleRedirectUris` を呼び出し側へ返す（呼び出し側が UI でユーザー選択させる）
 *  - 200 + success=true + redirectUris がすべて null: 成功 toast 1回（画面に留まるためユーザーへフィードバック、Sentry なし）
 *  - 200 + success=false: toast 1回（Sentry なし）
 *  - 200 + 不正スキームの URL: toast 1回 + Sentry 1回
 *  - 4xx: toast のみ（ユーザー/データ起因のため Sentry 送信なし）
 *  - 5xx / ネットワークエラー: toast 1回 + Sentry 1回（`SendToTrackingService`）
 *  - 非 Axios エラー: toast 1回 + Sentry.captureException 1回
 *
 * toast と Sentry の発火は各シナリオで必ず 1回ずつ（二重発火しない）。
 * `handledInTry` フラグにより、try 内で処理済みのエラーを catch 側が再処理しないようにしている。
 *
 * @returns `send` - 連携を実行する関数。複数 URL 返却時は `multipleRedirectUris` に格納されて返る。
 */
export const useSendToSamboKun = (): UseSendToSamboKunResult => {
  const { apiClient } = useApiClient();

  // fetcher: useSWRMutation の第2引数。arg にリクエストボディを受け取る。
  const fetcher = async (
    _url: string,
    { arg }: { arg: SendToPartnerRequest }
  ): Promise<SendToPartnerResponse> => {
    return await sendPropertyAndOwnerInfo(apiClient, SAMBO_KUN_PARTNER_ID, arg);
  };

  const { trigger } = useSWRMutation(
    `/partner/${SAMBO_KUN_PARTNER_ID}/property-and-owner-info`,
    fetcher
  );

  const send = async (params: SendTarget): Promise<SendResult> => {
    // 不正スキームなどの場合は catch 側のロギングを抑止するフラグ
    // （try 内で toast + Sentry を発火させた後 throw するが、catch 側が再度発火しないようにする）
    let handledInTry = false;

    // 除外 ID が渡されなかった場合に `excludedPictureRequestDetailIds: undefined`
    // を含めると、テストの `toHaveBeenCalledWith` で差異を確認しづらく、また
    // 呼び出し側がインメモリで body を検査するシナリオでも余計なキーが残る。
    // 条件付きで spread することでキー自体を生やさない。
    const body: SendToPartnerRequest =
      params.target === "owner"
        ? {
            realEstateOwnerAnalyzeRequestId: params.ownerAnalyzeRequestId,
            ...(params.excludedPictureRequestDetailIds != null && {
              excludedPictureRequestDetailIds:
                params.excludedPictureRequestDetailIds,
            }),
          }
        : {
            allMattersAnalyzeRequestId: params.allMattersAnalyzeRequestId,
            ...(params.excludedPictureRequestDetailIds != null && {
              excludedPictureRequestDetailIds:
                params.excludedPictureRequestDetailIds,
            }),
          };

    try {
      const res = await trigger(body);

      if (res == null) {
        // trigger が undefined を返す場合（型上の安全策）は unexpectedError 相当として扱う
        const unexpectedError = new Error("参謀くんAPIの応答が空でした");
        Sentry.captureException(unexpectedError);
        toast.error("リダイレクト先が取得できませんでした");
        handledInTry = true;
        throw unexpectedError;
      }

      // 200 OK + success が false のケース（BE の Fail-fast 設計では実質返らないが念のため）
      if (!res.success) {
        toast.error("参謀くんへの連携に失敗しました");
        handledInTry = true;
        throw new Error("参謀くんAPIの応答で success=false が返りました");
      }

      // 有効な redirect URI を取り出す。
      // redirectUri の scheme バリデーション（XSS / 任意スキーム実行対策）。
      // `javascript:` / `data:` / `http:` を弾き、`https:` のみ許可する。
      // URL scheme は RFC 3986 上 case-insensitive なので、`HTTPS://` などの
      // 大文字混じりでも許可するために `new URL()` でパースして `protocol` を比較する
      // （`protocol` は仕様上小文字化された値が返る）。
      //
      // NOTE: この検査は任意の HTTPS ホストを許可するため、open redirect
      // （攻撃者制御の HTTPS サイトへの誘導）は防げない。BE / 参謀くん側を信頼する
      // 設計のため現状はホスト名 allowlist を持たないが、将来的な強化として
      // 環境変数ベースの allowlist 導入を検討する（別 PR）。
      const isHttpsUri = (uri: string | null): uri is string => {
        if (uri == null) return false;
        try {
          return new URL(uri).protocol === "https:";
        } catch {
          return false;
        }
      };

      const validRedirectUris = res.redirectUris.filter(isHttpsUri);

      // 不正スキームの URL が含まれていないか確認（null や https: 以外の scheme は弾く）
      // 型ガード isHttpsUri を直接 find に渡すと no-confusing-void-expression が
      // 誤検知するため、述語関数を一段抽出する。
      const hasInvalidScheme = (uri: string | null): boolean => {
        return uri != null && !isHttpsUri(uri);
      };
      const invalidUri = res.redirectUris.find(hasInvalidScheme);

      if (invalidUri != null) {
        const parsed = (() => {
          try {
            return new URL(invalidUri);
          } catch {
            return null;
          }
        })();

        // 不正スキームは BE / 参謀くん側の異常応答とみなし致命的扱い
        const invalidUriError = new Error(
          `参謀くんAPIの応答の redirectUris に不正なスキームが含まれています: ${
            parsed?.protocol ?? "parse失敗"
          }`
        );
        Sentry.captureException(invalidUriError, {
          extra: { invalidUri },
        });
        toast.error("リダイレクト先が不正です");
        handledInTry = true;
        throw invalidUriError;
      }

      if (validRedirectUris.length === 0) {
        // すべて null の場合: リダイレクトしないが、エラーでもない（正常終了）。
        // ユーザーは画面に留まるため、無音だと完了を認識できない。成功 toast でフィードバックする。
        toast.success("参謀くんへの連携を受け付けました");
        return { multipleRedirectUris: [] };
      }

      if (validRedirectUris.length === 1) {
        // 単一の有効 URL: 従来通り自動リダイレクト（遷移そのものが成功フィードバック）
        navigateTo(validRedirectUris[0]);
        return { multipleRedirectUris: [] };
      }

      // 複数の有効 URL: 自動でどれか 1 つを選んでしまうと残りが失われユーザーが連携先を確認できない。
      // 呼び出し側でリストを表示し、ユーザーに選択 / 記録してもらう。
      return { multipleRedirectUris: validRedirectUris };
    } catch (error) {
      // try 内で toast + Sentry 発火済みの場合は catch 側のロギングをスキップ
      if (handledInTry) {
        throw error;
      }

      if (isAxiosError(error)) {
        // エラーレスポンスのランタイム型ガード（`as` キャストを避けて実行時安全性を確保）
        const rawData: unknown = error.response?.data;
        const data =
          rawData != null && typeof rawData === "object" ? rawData : null;

        const detail: string | undefined =
          data &&
          "detail" in data &&
          typeof (data as { detail: unknown }).detail === "string"
            ? (data as { detail: string }).detail
            : undefined;

        if (detail) {
          toast.error(detail);
        } else {
          toast.error("参謀くんへの連携に失敗しました");
        }

        // 5xx 系（サーバーエラー / BFF タイムアウト等）と response 不在のネットワークエラーを致命的とみなし Sentry へ送信。
        // 4xx 系（バリデーション・認可・重複等）はユーザー/データ起因なので送信しない。
        const statusCode = error.response?.status;
        if (
          statusCode == null ||
          statusCode >= HttpStatusCode.InternalServerError
        ) {
          SendToTrackingService(error);
        }
      } else {
        // 非 Axios エラーは予期しない実行時エラーなので致命的扱い
        toast.error("参謀くんへの連携に失敗しました");
        Sentry.captureException(error);
      }
      throw error;
    }
  };

  return { send };
};
